import * as amplitude from "@amplitude/unified"

import Firebase from "#classes/Firebase"
import { ANALYTICS_EVENTS, AnalyticsEvent } from "#classes/FirebaseAnalytics"
import { CLASS_GROUPS } from "#constants/classOptions"
import { DOC_TYPES } from "#customTypes/composedTypes/firebaseTypes"
import { StandoutCategory } from "#customTypes/composedTypes/standoutTypes"
import { Uuid } from "#customTypes/globalTypes"

const AMPLITUDE_API_KEY = import.meta.env.VITE_AMPLITUDE_API_KEY
const DEBUG_MODE = import.meta.env.VITE_DEBUG_MODE === "true"

class AmplitudeAnalyticsSingleton {
  private isInitialized = false
  private appVersion = "unknown"
  private appChangelog = ""
  private userId: string | null = null
  private userRole: string | null = null
  private isImpersonating = false
  private currentPage: string | null = null

  initialize() {
    if (this.isInitialized) return

    if (!AMPLITUDE_API_KEY) {
      console.error("❌ Amplitude API key not found")
      return
    }

    console.log("✅ API key found. Initializing Amplitude Client.")

    amplitude.initAll(AMPLITUDE_API_KEY, {
      analytics: {
        autocapture: {
          formInteractions: false,
          attribution: true,
          fileDownloads: true,
          pageViews: true,
          sessions: true,
          networkTracking: true,
          webVitals: true,
          frustrationInteractions: true,
        },
      },
    })

    this.isInitialized = true
    this.fetchAppVersion()
  }

  // We reload the app if the version changes so this should be sufficient
  private fetchAppVersion = async () => {
    try {
      const versionDocs = await Firebase.getInstance().variableQuery<{
        version: string
        id: string
        changelogItems?: string[]
      }>(DOC_TYPES.VERSIONS, {
        queries: {},
        orderBy: { field: "version", direction: "desc" },
        limitCount: 1,
      })
      const latestVersion = versionDocs[0]
      this.appVersion = latestVersion?.version ?? "unknown"
      this.appChangelog = latestVersion?.changelogItems?.join("|") ?? ""
    } catch (error) {
      console.error("Failed to fetch app version:", error)
    }
  }

  getRoleString = (role: string | null, isImpersonating: boolean) => {
    return role ? `${role}${isImpersonating ? ` (Impersonating)` : ""}` : null
  }

  setUser = (
    userId: string,
    role: string | null,
    actualRole: string | null
  ) => {
    if (!this.isInitialized) return

    if (this.userId === userId) return

    this.userId = userId
    this.userRole = role
    this.isImpersonating = role !== actualRole

    amplitude.setUserId(userId)

    if (this.userId == null) {
      this.trackEvent(ANALYTICS_EVENTS.USER_LOGOUT)
    } else {
      this.trackEvent(ANALYTICS_EVENTS.USER_LOGIN, {
        role: this.getRoleString(role, this.isImpersonating),
        isImpersonating: this.isImpersonating,
      })
    }
  }

  // Core tracking method
  trackEvent = (
    eventName: AnalyticsEvent,
    parameters: Record<string, unknown> = {}
  ) => {
    if (!this.isInitialized) {
      console.error("Analytics not initialized")
      return
    }

    if (!this.userId) {
      console.error("trackEvent: User not set", eventName, parameters)
      return
    }

    if (DEBUG_MODE) {
      console.log("DEBUG_MODE trackEvent: ", eventName, parameters)
      return
    }

    const requiredParameters = {
      userId: this.userId,
      appVersion: this.appVersion,
      appChangelog: this.appChangelog || undefined,
      role: `${this.userRole}${this.isImpersonating ? ` (Impersonating)` : ""}`,
    }

    amplitude.track(eventName, {
      ...parameters,
      ...requiredParameters,
    })
  }

  // Convenience methods
  trackPageView = (page: string) => {
    if (this.currentPage === page) return

    this.currentPage = page
    this.trackEvent(ANALYTICS_EVENTS.PAGE_VIEW, { page })
  }

  trackUserPinMissing = (message: string) => {
    console.log("🔥🔥🔥 USER PIN MISSING!")
    this.trackEvent(ANALYTICS_EVENTS.ERROR_OCCURRED, {
      errorMessage: message,
      errorStack: "User pin missing",
      context: "Trying to figure out why pins go missing",
    })
  }

  trackClassAction = (
    action:
      | typeof ANALYTICS_EVENTS.CLASS_CREATED
      | typeof ANALYTICS_EVENTS.CLASS_UPDATED
      | typeof ANALYTICS_EVENTS.CLASS_DELETED
      | typeof ANALYTICS_EVENTS.CLASS_VIEWED,
    parameters: {
      classId: Uuid
      className: string
    }
  ) => {
    this.trackEvent(action, parameters)
  }

  trackStudentAction = (
    action:
      | typeof ANALYTICS_EVENTS.STUDENT_ADDED
      | typeof ANALYTICS_EVENTS.STUDENT_UPDATED
      | typeof ANALYTICS_EVENTS.STUDENT_DELETED
      | typeof ANALYTICS_EVENTS.STUDENT_VIEWED,
    parameters: {
      studentId: Uuid
      studentName: string
    }
  ) => {
    this.trackEvent(action, parameters)
  }

  trackStudentEnrollmentAction = (
    action:
      | typeof ANALYTICS_EVENTS.STUDENT_ADDED_TO_CLASS
      | typeof ANALYTICS_EVENTS.STUDENT_REMOVED_FROM_CLASS
      | typeof ANALYTICS_EVENTS.STUDENT_ENROLLMENT_UPDATED,
    parameters: {
      studentId: Uuid
      classId: Uuid
      sessionCount: number
    }
  ) => {
    this.trackEvent(action, parameters)
  }

  trackTeacherEnrollmentAction = (
    action:
      | typeof ANALYTICS_EVENTS.TEACHER_ADDED_TO_CLASS
      | typeof ANALYTICS_EVENTS.TEACHER_REMOVED_FROM_CLASS
      | typeof ANALYTICS_EVENTS.TEACHER_ENROLLMENT_UPDATED,
    parameters: {
      teacherId: Uuid
      classId: Uuid
      sessionCount: number
    }
  ) => {
    this.trackEvent(action, parameters)
  }

  trackAssistantEnrollmentAction = (
    action:
      | typeof ANALYTICS_EVENTS.ASSISTANT_ADDED_TO_CLASS
      | typeof ANALYTICS_EVENTS.ASSISTANT_REMOVED_FROM_CLASS
      | typeof ANALYTICS_EVENTS.ASSISTANT_ENROLLMENT_UPDATED,
    parameters: {
      studentId: Uuid
      classId: Uuid
      sessionCount: number
    }
  ) => {
    this.trackEvent(action, parameters)
  }

  trackTryoutAction = (
    action:
      | typeof ANALYTICS_EVENTS.TRYOUT_SCHEDULED
      | typeof ANALYTICS_EVENTS.TRYOUT_CANCELLED,
    parameters: {
      tryoutId: Uuid
      studentId: Uuid
      sessionId: Uuid
    }
  ) => {
    this.trackEvent(action, parameters)
  }

  trackMakeupAction = (
    action:
      | typeof ANALYTICS_EVENTS.MAKEUP_SCHEDULED
      | typeof ANALYTICS_EVENTS.MAKEUP_CANCELLED,
    parameters: {
      makeupId: Uuid
      studentId: Uuid
      missedClassId: Uuid
      missedSessionId: Uuid
      makeupClassId: Uuid | null | undefined
      makeupSessionId: Uuid | null | undefined
      isMakeupScheduled: boolean
    }
  ) => {
    this.trackEvent(action, parameters)
  }

  trackCurriculumAction = (
    action:
      | typeof ANALYTICS_EVENTS.CURRICULUM_CREATED
      | typeof ANALYTICS_EVENTS.CURRICULUM_UPDATED
      | typeof ANALYTICS_EVENTS.CURRICULUM_DELETED,
    parameters: {
      curriculumId: Uuid
      curriculumName: string
    }
  ) => {
    this.trackEvent(action, parameters)
  }

  trackProjectAction = (
    action:
      | typeof ANALYTICS_EVENTS.PROJECT_CREATED
      | typeof ANALYTICS_EVENTS.PROJECT_UPDATED,
    parameters: {
      projectId: Uuid
      projectName: string
    }
  ) => {
    this.trackEvent(action, parameters)
  }

  trackAttendanceAction = (parameters: {
    studentId: Uuid
    classId: Uuid
    sessionId: Uuid
    status: string
  }) => {
    console.log("✅🔥 trackAttendanceAction => ", parameters)
    this.trackEvent(ANALYTICS_EVENTS.ATTENDANCE_UPDATED, parameters)
  }

  trackEvaluationAction = (
    action:
      | typeof ANALYTICS_EVENTS.EVALUATION_STARTED
      | typeof ANALYTICS_EVENTS.EVALUATION_COMPLETED
      | typeof ANALYTICS_EVENTS.EVALUATION_SAVED,
    parameters: {
      studentId: Uuid
      classId: Uuid
      classGroup: CLASS_GROUPS
    }
  ) => {
    this.trackEvent(action, parameters)
  }

  trackError = (error: Error, context?: string) => {
    this.trackEvent(ANALYTICS_EVENTS.ERROR_OCCURRED, {
      errorMessage: error.message,
      errorStack: error.stack,
      context,
    })
  }

  trackModalAction = (
    action:
      | typeof ANALYTICS_EVENTS.MODAL_OPENED
      | typeof ANALYTICS_EVENTS.MODAL_CLOSED,
    parameters: {
      modalName: string
    }
  ) => {
    this.trackEvent(action, parameters)
  }

  trackLoadTime = (page: string, loadTime: number) => {
    this.trackEvent(ANALYTICS_EVENTS.LOAD_TIME, {
      page,
      loadTimeMs: loadTime,
    })
  }

  trackHelpRequestAction = (
    action:
      | typeof ANALYTICS_EVENTS.HELP_REQUEST_CREATED
      | typeof ANALYTICS_EVENTS.HELP_REQUEST_ACKNOWLEDGED
      | typeof ANALYTICS_EVENTS.HELP_REQUEST_RESOLVED,
    parameters: {
      helpRequestId: Uuid
      room?: string
      priority?: string
      comment?: string
      timeToAckMs?: number
      timeToResolveMs?: number
    }
  ) => {
    this.trackEvent(action, parameters)
  }

  trackImpersonationStarted = (
    adminUserId: Uuid | null,
    targetUserId: Uuid
  ) => {
    this.trackEvent(ANALYTICS_EVENTS.IMPERSONATION_STARTED, {
      adminUserId,
      targetUserId,
      userAgent: navigator.userAgent,
    })
  }

  trackImpersonationStopped = (
    adminUserId: Uuid | null,
    targetUserId: Uuid,
    durationMs: number
  ) => {
    this.trackEvent(ANALYTICS_EVENTS.IMPERSONATION_STOPPED, {
      adminUserId,
      targetUserId,
      durationMs,
    })
  }

  trackStandoutAction = (
    action:
      | typeof ANALYTICS_EVENTS.STANDOUT_MOMENT_SAVED
      | typeof ANALYTICS_EVENTS.STANDOUT_FEEDBACK_GENERATED
      | typeof ANALYTICS_EVENTS.STANDOUT_FEEDBACK_REAPPLIED,
    parameters: {
      teacherId: Uuid | null
      studentId: Uuid
      category: StandoutCategory
      tagCount: number
      tagTexts?: string[]
      hasNote?: boolean
      classId?: Uuid
      evaluationId?: Uuid
      persona?: string
      writingStyle?: string
    }
  ) => {
    this.trackEvent(action, parameters)
  }
}

// Singleton export
const AmplitudeAnalytics = new AmplitudeAnalyticsSingleton()
export default AmplitudeAnalytics
